import { getLocaleNumberingSystem } from '@syncanix/shared';

import type { Locale } from '@syncanix/shared';

/**
 * Locale-native number formatting for the marketing site.
 *
 * **Why this exists:** a bare `n.toLocaleString(locale)` lets the digit system
 * follow the host runtime's ICU defaults, which drift by ICU version — so the
 * same number can render differently to two readers of the same locale. These
 * helpers pin the numbering system explicitly, deterministically, on any build.
 *
 * The decision itself is owned by `getLocaleNumberingSystem` in
 * `@syncanix/shared` (the cross-app single source of truth — web + widget pin
 * the same way), and per the locked spec the default is **Western (`latn`) for
 * every locale including Arabic**. Eastern Arabic-Indic (٠-٩) is returned only
 * for Arabic when a caller passes the `numerals: 'eastern'` USER preference —
 * which the marketing site does not carry, so marketing numbers are Western
 * throughout and only the grouping varies by locale. (This comment previously
 * claimed the opposite: that `ar` renders Arabic-Indic here. It never did, and
 * a reader who trusted it would document a rendering the product does not
 * produce.)
 *
 * See `product/trust/localization-rtl.md`. Pure (no React) so data modules,
 * components, and tests can all use it. A caller may still override
 * `numberingSystem` via `options` for a one-off.
 */

/** The digit (numbering) system a locale renders numbers in. */
export function localeNumberingSystem(locale: Locale): string {
  return getLocaleNumberingSystem(locale);
}

/** An `Intl.NumberFormat` for `locale` with its native digit system pinned. */
export function localeNumberFormat(
  locale: Locale,
  options: Intl.NumberFormatOptions = {},
): Intl.NumberFormat {
  return new Intl.NumberFormat(locale, {
    numberingSystem: localeNumberingSystem(locale),
    ...options,
  });
}

/** Format `value` for `locale` in its native digits (Arabic-Indic for `ar`). */
export function formatLocaleNumber(
  value: number,
  locale: Locale,
  options?: Intl.NumberFormatOptions,
): string {
  return localeNumberFormat(locale, options).format(value);
}

/**
 * Join numbers into an "either / or" list in the locale's own idiom —
 * "3, 6, 9, or 12" in English, "3 أو 6 أو 9 أو 12" in Arabic, "3, 6, 9 oder 12"
 * in German.
 *
 * `type: 'disjunction'` is the load-bearing choice, and it is a meaning decision
 * rather than a style one. The list this exists for is the closed set of posted
 * prices, where an interaction costs exactly ONE of the four — so a conjunction
 * ("3, 6, 9 and 12") would read as though all four were charged together, which
 * is the opposite of the promise. `type: 'unit'` is not the escape either: at
 * `narrow` it drops the separator entirely in four of the six locales ("3 6 9
 * 12"), which is ambiguous, and at `short` it still emits a conjunction in
 * French, German and Arabic. Disjunction is the only form that is both
 * unambiguous and idiomatic in all six.
 *
 * Each number still goes through {@link formatLocaleNumber}, so the digit system
 * stays pinned by the same single source of truth; only the separator and the
 * conjunction word come from ICU, and both are far more stable across ICU
 * versions than digit data is.
 */
export function formatLocaleNumberList(values: readonly number[], locale: Locale): string {
  const parts = values.map((value) => formatLocaleNumber(value, locale));
  return new Intl.ListFormat(locale, { style: 'long', type: 'disjunction' }).format(parts);
}
